import { useState, useRef, useCallback, useEffect } from "react";
import { Card, Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { FULL_RECT, COLLAPSE_BL } from "./utils/wipeAnimationUtils";
import { useWipeAnimation } from "./hooks/useWipeAnimation";
import { useRenderProfiler } from "@/utils/renderProfiler";

/**
 * Single card component shared by clip/key-moment cards and ingestion job cards.
 *
 * Owns:
 *  - Flip state (flipped / setFlipped)
 *  - Wipe animation refs and hook
 *  - Front Card shell (dark bg, 16:9, overflow hidden)
 *  - Gradient overlay
 *  - Summary overlay box (bottom bar, always visible)
 *  - Details overlay box (bottom panel, revealed on hover via wipe animation)
 *  - White wipe-line div
 *  - Orange back Card shell (gradient, scrollbar styles, dark inner overlay)
 *
 * Callers provide all content via render props; player refs and play state stay
 * in the caller and are captured via closure.
 *
 * @param {object}   props
 * @param {boolean}  [props.showedBack=true]      - attach wipe listeners (false = no hover anim)
 * @param {number}   [props.minWidth]              - Grid min-width (defaults to 16:9 × 160px)
 * @param {object}   [props.gridSx]               - extra sx merged into the outer Grid
 * @param {string}   [props.outline]               - CSS outline on the flip Box (selection state)
 * @param {Function} [props.onClick]               - click handler on the outer flip Box
 * @param {Function} props.renderVideo             - () => ReactNode — background media
 * @param {Function} props.renderSummary           - () => ReactNode — summary bar content
 * @param {Function} props.renderDetails           - () => ReactNode — details overlay content
 * @param {Function} props.renderBack              - () => ReactNode — orange back face content
 */
export default function WipeCard({
    showedBack = true,
    minWidth = (160 * 16) / 9,
    gridSx,
    outline,
    onClick,
    renderVideo,
    renderSummary,
    renderDetails,
    renderBack,
    onHoverEnter,
    onHoverLeave,
}) {
    useRenderProfiler("WipeCard", {
        showedBack, minWidth, gridSx, outline, onClick,
        renderVideo, renderSummary, renderDetails, renderBack, onHoverEnter, onHoverLeave,
    });

    const [flipped, setFlipped] = useState(false);
    const [frontMounted, setFrontMounted] = useState(true);
    // The orange back face (renderBack) is heavy (TextFields, AutoComplete, Rating).
    // Mount it lazily on hover so off-screen / idle cards don't pay for it. Despawn
    // again once the pointer leaves and the card is not flipped open.
    const [backMounted, setBackMounted] = useState(false);

    const cardRef    = useRef(null);
    const summaryRef = useRef(null);
    const detailsRef = useRef(null);
    const lineRef    = useRef(null);
    const flipBoxRef = useRef(null);
    const flippedRef = useRef(false);   // latest flip state, read inside hover callbacks
    const despawnRef = useRef(null);    // pending back-face unmount handle

    const cancelDespawn = useCallback(() => {
        if (despawnRef.current == null) return;
        const { type, id } = despawnRef.current;
        if (type === "idle" && typeof cancelIdleCallback === "function") cancelIdleCallback(id);
        else clearTimeout(id);
        despawnRef.current = null;
    }, []);

    const scheduleDespawn = useCallback(() => {
        cancelDespawn();
        const run = () => {
            despawnRef.current = null;
            if (!flippedRef.current) setBackMounted(false);
        };
        if (typeof requestIdleCallback === "function") {
            despawnRef.current = { type: "idle", id: requestIdleCallback(run, { timeout: 600 }) };
        } else {
            despawnRef.current = { type: "timeout", id: setTimeout(run, 400) };
        }
    }, [cancelDespawn]);

    // Prewarm the back face on hover: the pointer always enters before a flip click,
    // so renderBack renders while idle and the flip stays smooth.
    const handleHoverEnter = useCallback(() => {
        cancelDespawn();
        setBackMounted(true);
        onHoverEnter?.();
    }, [cancelDespawn, onHoverEnter]);

    const handleHoverLeave = useCallback(() => {
        if (!flippedRef.current) scheduleDespawn();
        onHoverLeave?.();
    }, [scheduleDespawn, onHoverLeave]);

    // Cancel any pending despawn on unmount.
    useEffect(() => cancelDespawn, [cancelDespawn]);

    // Re-run when frontMounted changes so wipe listeners re-attach after the front Card remounts.
    useWipeAnimation({
        cardRef, summaryRef, detailsRef, lineRef,
        enabled: showedBack,
        deps: [flipped, frontMounted],
        onHoverEnter: handleHoverEnter,
        onHoverLeave: handleHoverLeave,
    });

    const handleSetFlipped = useCallback((value) => {
        if (value) {
            flippedRef.current = true;
            cancelDespawn();   // keep the back mounted while it's shown
            setFlipped(true);
            const box = flipBoxRef.current;
            const onEnd = (e) => {
                if (e.propertyName === "transform") {
                    box.removeEventListener("transitionend", onEnd);
                    setFrontMounted(false);
                }
            };
            box?.addEventListener("transitionend", onEnd);
        } else {
            flippedRef.current = false;
            setFrontMounted(true);
            requestAnimationFrame(() => requestAnimationFrame(() => setFlipped(false)));
        }
    }, [cancelDespawn]);

    return (
        <Grid sx={{
            fontFamily: "IBM Plex Sans, sans-serif !important",
            pb: 1,
            perspective: "1000px",
            minWidth: `${minWidth}px`,
            ...gridSx,
        }}>
            <Box
                ref={flipBoxRef}
                onClick={onClick ? (e) => { e.stopPropagation(); onClick(e); } : (e) => e.stopPropagation()}
                sx={{
                    position: "relative",
                    width: "100%",
                    borderRadius: 2,
                    ...(outline && { outline }),
                    aspectRatio: "16 / 9",
                    transformStyle: "preserve-3d",
                    transition: "transform 0.6s",
                    transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
            >
                {/* ── FRONT FACE ─────────────────────────────────────────── */}
                {frontMounted && (
                    <Card
                        ref={cardRef}
                        elevation={0}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backfaceVisibility: "hidden",
                            borderRadius: 2,
                            overflow: "hidden",
                            cursor: "pointer",
                            userSelect: "none",
                            bgcolor: "#111",
                            isolation: "isolate",
                            outlineOffset: "-3px",
                        }}
                    >
                        {/* Media + gradient overlay */}
                        <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
                            {renderVideo?.({ setFlipped: handleSetFlipped })}
                            <Box sx={{
                                position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none",
                                background: "linear-gradient(180deg, rgba(0,0,0,0.1), rgba(0,0,0,0.55))",
                            }} />
                        </Box>

                        {/* Summary overlay — always visible */}
                        {renderSummary && (
                            <Box
                                ref={summaryRef}
                                sx={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none", clipPath: FULL_RECT }}
                            >
                                <Box sx={{
                                    position: "absolute", left: 0, right: 0, bottom: 0,
                                    p: 2, py: 1,
                                    bgcolor: "rgba(0,0,0,0.45)",
                                    backdropFilter: "blur(8px)",
                                }}>
                                    {renderSummary()}
                                </Box>
                            </Box>
                        )}

                        {/* Details overlay — revealed on hover via wipe */}
                        {renderDetails && (
                            <Box
                                ref={detailsRef}
                                sx={{ position: "absolute", inset: 0, zIndex: 2, clipPath: COLLAPSE_BL }}
                            >
                                <Box sx={{
                                    position: "absolute", left: 0, right: 0, bottom: 0,
                                    minHeight: "65%", p: 2, py: 1,
                                    bgcolor: "rgba(0,0,0,0.55)",
                                    backdropFilter: "blur(10px)",
                                }}>
                                    {renderDetails({ setFlipped: handleSetFlipped })}
                                </Box>
                            </Box>
                        )}

                        {/* Wipe line */}
                        <div
                            ref={lineRef}
                            style={{
                                position: "absolute", inset: 0, zIndex: 5,
                                pointerEvents: "none", background: "white", opacity: 0.9,
                            }}
                        />
                    </Card>
                )}

                {/* ── BACK FACE ──────────────────────────────────────────── */}
                <Card
                    onClick={(e) => { e.stopPropagation(); handleSetFlipped(false); }}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                        borderRadius: 2,
                        overflowY: "auto",
                        cursor: "pointer",
                        background: "linear-gradient(135deg, orange, #da501aff)",
                        display: "flex",
                        flexDirection: "column",
                        color: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        "&::-webkit-scrollbar":            { width: "8px" },
                        "&::-webkit-scrollbar-track":      { background: "transparent" },
                        "&::-webkit-scrollbar-thumb":      { background: "rgba(255,255,255,0.3)", borderRadius: "10px" },
                        "&::-webkit-scrollbar-thumb:hover":{ background: "rgba(255,255,255,0.5)" },
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(255,255,255,0.3) transparent",
                    }}
                >
                    <Box sx={{
                        position: "absolute", bottom: 0, width: "100%", height: "100%",
                        bgcolor: "rgba(0,0,0,0.2)", p: 2,
                        borderTopLeftRadius: 8, borderTopRightRadius: 8,
                        boxShadow: 3,
                        display: "flex", flexDirection: "column",
                        alignItems: "flex-start", justifyContent: "flex-start",
                        overflowY: "auto",
                    }}>
                        {backMounted && renderBack?.({ setFlipped: handleSetFlipped })}
                    </Box>
                </Card>
            </Box>
        </Grid>
    );
}
